import React from 'react';
import { Box, Typography } from '@mui/material';
import { InboxOutlined as InboxOutlinedIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';

export interface EmptyStateProps {
  /** Primary line (default: “No data found”) */
  title?: string;
  /** Optional supporting copy */
  description?: string;
  /** Replace default inbox icon */
  icon?: React.ReactNode;
  /** Tighter layout for inside table cells or small panels */
  compact?: boolean;
  /** Minimum height when not compact */
  minHeight?: number | string;
  /** Extra content (e.g. CTA button) */
  children?: React.ReactNode;
}

const DEFAULT_TITLE = 'No data found';

/**
 * Consistent empty / no-results presentation across tables, lists, and sections.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = DEFAULT_TITLE,
  description,
  icon,
  compact = false,
  minHeight = 200,
  children,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: compact ? 2 : 4,
      px: compact ? 1 : 2,
      minHeight: compact ? undefined : minHeight,
      color: SEARCE_COLORS.textSecondary,
    }}
  >
    <Box
      sx={{
        mb: compact ? 1 : 1.5,
        opacity: 0.85,
        '& .MuiSvgIcon-root': { fontSize: compact ? 36 : 48, color: SEARCE_COLORS.neutral_400 },
      }}
    >
      {icon ?? <InboxOutlinedIcon />}
    </Box>
    <Typography
      variant={compact ? 'body2' : 'subtitle1'}
      sx={{
        fontWeight: 600,
        color: SEARCE_COLORS.textPrimary,
        mb: description ? 0.5 : 0,
        maxWidth: 360,
      }}
    >
      {title}
    </Typography>
    {description != null && description !== '' && (
      <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary, maxWidth: 400 }}>
        {description}
      </Typography>
    )}
    {children != null && <Box sx={{ mt: 2 }}>{children}</Box>}
  </Box>
);
